// Trains (§31 S7, design "Train Track", "Train Engine", "Train Car").
//
// **Track** is an ordinary plowed structure (kind 16, 1 HP). An **Engine**
// (17) and a **Car** (18) are obstructions built over an own Track: one
// structure per square still, so a train *replaces* the track it stands on
// and the track is implied beneath it — laid back fresh when the train moves
// off. An engine drives forward along own track to a square it was ordered
// to (`drive-train`), up to `TRAINS.squaresPerPoint` squares a tick for one
// fuel point, burned from `TRAINS.coalPerPoint` coal it holds; its cars
// follow in its trail. A car carries up to two of its owner's pieces off the
// board (`board-train` / `alight-train`). The only thing that moves a piece
// without a move — and movegen cannot see any of it: trains move inside the
// tick, which is handed an `IndustryState`, so no search or perft reaches it.
//
// Imports no value from `structures.ts`, which calls into this file.

import {
  type Color,
  DIR_OFFSET,
  type Direction,
  fileOf,
  KING,
  type PieceType,
  pieceColor,
  pieceType,
  rankOf,
  type Side
} from "../board";
import { isAttackedByEnemyOf } from "../chess/attacks";
import { COAL, FUEL } from "../materials";
import {
  armourLeft,
  areEnemies,
  claspedTo,
  crownedFrom,
  facingAt,
  hasCell,
  hurtAt,
  imitatingAt,
  invalidateMoves,
  obstructsAt,
  pieceAt,
  pieceIdAt,
  platingAt,
  type Position,
  removePiece,
  royalsOf,
  setCrowned,
  setFacing,
  setHurt,
  setImitating,
  setObstruction,
  setPiece,
  setPieceId,
  setPlating,
  setStructureHealth,
  setStructureOwner,
  setVariant,
  setVitality,
  variantAt,
  vitalityAt
} from "../position";
import { PIECE_VALUE, STRUCTURE_ROWS, structureRow, TRAINS } from "../rules";
import {
  addHeap,
  clearDump,
  cookingCount,
  cookingUnitAt,
  dropOnGround,
  type IndustryState,
  markHeap,
  type Rider,
  setHandoff,
  setLoadedAt,
  setMinedAt,
  setPreachedAt,
  setReserve,
  setStructure,
  stripHoldings,
  type Structure,
  structureAt,
  structureSquares,
  TRACK,
  type Train,
  TRAIN_CAR,
  TRAIN_ENGINE,
  unitAt,
  unitCount,
  unitGrade,
  unitLine
} from "./state";

/** Why a train order is refused (the reducer's and the session's wording). */
export type TrainError =
  | "not-your-engine"
  | "not-your-car"
  | "not-your-piece"
  | "no-route"
  | "not-beside-car"
  | "royal"
  | "held"
  | "car-full"
  | "car-empty"
  | "loads-spent"
  | "unloads-spent"
  | "not-orthogonal"
  | "square-blocked";

const isTrainKind = (kind: number): boolean => structureRow(kind).onTrack === true;

/** A train (engine or car) of `owner`'s on `sq`, or undefined. */
function trainAt(industry: IndustryState, sq: number, kind: number, owner?: Color): Structure | undefined {
  const structure = structureAt(industry, sq);
  if (!structure || structure.kind !== kind) return undefined;
  if (owner !== undefined && structure.owner !== owner) return undefined;
  return structure;
}

/** Is `sq` own railway for `owner` — a Track, or a train standing on one? */
export function railed(industry: IndustryState, sq: number, owner: Color): boolean {
  const structure = structureAt(industry, sq);
  if (!structure || structure.owner !== owner) return false;
  return structure.kind === TRACK || isTrainKind(structure.kind);
}

/** A fresh Track, as `newStructure` would make one. */
export function newTrack(owner: Color): Structure {
  const row = STRUCTURE_ROWS[TRACK];
  return { kind: TRACK, owner, stock: [], accept: 0, offer: 0, paidGrade: row.bill[0].grade, health: row.health };
}

/** Write one square's mirror from the industry's record. */
function mirror(position: Position, industry: IndustryState, sq: number): void {
  const structure = structureAt(industry, sq);
  setStructureOwner(position, sq, structure ? structure.owner : null);
  const row = structure ? structureRow(structure.kind) : undefined;
  setStructureHealth(position, sq, structure ? Math.min(structure.health ?? row!.health, row!.health) : 0);
  setObstruction(position, sq, row?.obstruction === true);
}

/** The engine pulling the car on `car`, or -1 when it is stranded. */
export function engineOf(industry: IndustryState, car: number): number {
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    if (structure.kind === TRAIN_ENGINE && structure.train?.cars?.includes(car)) return sq;
  }
  return -1;
}

// --- coupling ---------------------------------------------------------------

/** Orthogonal neighbours, N E S W. */
const orthogonal = (sq: number): number[] => DIR_OFFSET.map((d) => sq + d);

/**
 * The own engine a car built on `at` would couple to: one whose tail (the
 * engine itself with no cars, else its last car) is orthogonally beside `at`
 * and which pulls fewer than `TRAINS.maxCars`. Lowest engine square first.
 * -1 when none.
 */
export function couplingFor(industry: IndustryState, at: number, owner: Color): number {
  const beside = orthogonal(at);
  for (const sq of structureSquares(industry)) {
    const engine = trainAt(industry, sq, TRAIN_ENGINE, owner);
    if (!engine) continue;
    const cars = engine.train?.cars ?? [];
    if (cars.length >= TRAINS.maxCars) continue;
    const tail = cars.length > 0 ? cars[cars.length - 1] : sq;
    if (beside.includes(tail)) return sq;
  }
  return -1;
}

/**
 * A train goes up on `at` over an own Track (the build's last step): the
 * track is implied beneath it from now on. An engine starts with no order;
 * a car couples to `couplingFor`'s engine, and an engine that has never
 * moved takes its heading away from its first car.
 */
export function standTrain(industry: IndustryState, at: number, structure: Structure): void {
  if (structure.kind === TRAIN_ENGINE) {
    structure.train = { heading: -1, dest: -1, cars: [] };
    return;
  }
  structure.train = { riders: [] };
  const engineSq = couplingFor(industry, at, structure.owner);
  if (engineSq < 0) return;
  const engine = structureAt(industry, engineSq)!;
  const train = (engine.train ??= { heading: -1, dest: -1, cars: [] });
  train.cars = [...(train.cars ?? []), at];
  if (train.cars.length === 1 && (train.heading ?? -1) < 0) {
    const toCar = DIR_OFFSET.indexOf(at - engineSq);
    if (toCar >= 0) train.heading = (toCar + 2) % 4;
  }
}

/**
 * A train leaves the board (wrecked, demolished): uncouple it. A car takes
 * every car behind it off its engine — they stand stranded; an engine's cars
 * are stranded with it. A car's riders die with it, each leaving its scrap
 * (the owner may not dismantle an occupied car — `demolish` refuses).
 */
export function releaseTrain(industry: IndustryState, at: number, structure: Structure): void {
  if (structure.kind === TRAIN_CAR) {
    const engineSq = engineOf(industry, at);
    if (engineSq >= 0) {
      const train = structureAt(industry, engineSq)!.train!;
      train.cars = train.cars!.slice(0, train.cars!.indexOf(at));
    }
    for (const rider of structure.train?.riders ?? []) {
      addHeap(industry, at, PIECE_VALUE[pieceType(rider.piece)]);
      markHeap(industry, at, pieceColor(rider.piece), pieceType(rider.piece));
    }
  }
}

// --- driving ----------------------------------------------------------------

/**
 * The forward route from the engine on `from` to `dest` along `owner`'s own
 * railway, or null (design: "if it could do so by moving directly forward").
 * Breadth-first over (square, heading): never a reversal, straight before
 * right before left, so the shortest route and its tie-break are defined.
 * The squares stepped onto, `dest` last; `[]` when already there.
 */
export function trainRoute(
  industry: IndustryState,
  owner: Color,
  from: number,
  heading: number,
  dest: number
): number[] | null {
  if (from === dest) return [];
  const key = (sq: number, dir: number): number => sq * 4 + dir;
  const parent = new Map<number, number>();
  const queue: [sq: number, dir: number][] = [];
  const turns = (dir: number): number[] => (dir < 0 ? [0, 1, 2, 3] : [dir, (dir + 1) % 4, (dir + 3) % 4]);
  const visit = (sq: number, dir: number, back: number): void => {
    const k = key(sq, dir);
    if (parent.has(k)) return;
    parent.set(k, back);
    queue.push([sq, dir]);
  };
  for (const dir of turns(heading)) {
    const next = from + DIR_OFFSET[dir];
    if (railed(industry, next, owner)) visit(next, dir, -1);
  }
  for (let head = 0; head < queue.length; head++) {
    const [sq, dir] = queue[head];
    if (sq === dest) {
      const path: number[] = [];
      let k = key(sq, dir);
      while (k >= 0) {
        path.push(Math.floor(k / 4));
        k = parent.get(k)!;
      }
      return path.reverse();
    }
    for (const turn of turns(dir)) {
      const next = sq + DIR_OFFSET[turn];
      if (next !== from && railed(industry, next, owner)) visit(next, turn, key(sq, dir));
    }
  }
  return null;
}

/** Why `owner` cannot order the engine on `at` to `to`, or null. */
export function driveError(industry: IndustryState, owner: Color, at: number, to: number): TrainError | null {
  const engine = trainAt(industry, at, TRAIN_ENGINE, owner);
  if (!engine) return "not-your-engine";
  if (to === at) return null;
  return trainRoute(industry, owner, at, engine.train?.heading ?? -1, to) ? null : "no-route";
}

/** Order the engine on `at` to `to` (`to === at` stops it). The caller asked `driveError`. */
export function orderTrain(industry: IndustryState, at: number, to: number): void {
  const engine = structureAt(industry, at)!;
  engine.train = { ...(engine.train ?? {}), dest: to === at ? -1 : to };
}

const isCoal = (unit: number): boolean => unitLine(unit) === FUEL && unitGrade(unit) === COAL;
const coalIn = (structure: Structure): number => structure.stock.filter(isCoal).length;

/** Spend `count` coal from an engine's stock. */
function burnCoal(structure: Structure, count: number): void {
  for (let i = 0; i < count; i++) {
    const at = structure.stock.findIndex(isCoal);
    if (at >= 0) structure.stock.splice(at, 1);
  }
}

/**
 * Move a consist from `from` to `to` (same length, engine first). The bodies
 * travel; a square left gets fresh own Track back, a square entered had its
 * Track consumed into the train. Mirror written square by square.
 */
function shiftConsist(position: Position, industry: IndustryState, owner: Color, from: number[], to: number[]): void {
  const bodies = from.map((sq) => structureAt(industry, sq)!);
  for (const sq of from) setStructure(industry, sq, null);
  for (const sq of from) {
    if (!to.includes(sq)) setStructure(industry, sq, newTrack(owner));
  }
  for (let i = 0; i < to.length; i++) setStructure(industry, to[i], bodies[i]);
  const engine = bodies[0];
  engine.train = { ...engine.train, cars: to.slice(1) };
  for (const sq of new Set([...from, ...to])) mirror(position, industry, sq);
}

/** Royals of `owner` attacked now — the set a step may not grow. */
function attackedRoyals(position: Position, owner: Color): number[] {
  return royalsOf(position, owner).filter((sq) => isAttackedByEnemyOf(position, sq, owner));
}

/**
 * Every `owner` engine with an order drives (§31 S7), once a tick, by
 * engine square. One fuel point a moving tick — three coal burned, the
 * design's rate, at most once a tick — moves it up to five squares along
 * `trainRoute`; it waits (burning nothing) while the next square is held by
 * a piece or another train, or it holds under three coal. A step that would
 * expose one of its owner's royals is not taken. An order with no route left
 * is dropped. Answers squares moved.
 */
export function runTrains(position: Position, industry: IndustryState, owner: Color): number {
  let moved = 0;
  const engines = structureSquares(industry).filter((sq) => trainAt(industry, sq, TRAIN_ENGINE, owner));
  for (const start of engines) {
    const engine = structureAt(industry, start)!;
    const train = engine.train;
    if (!train || train.dest === undefined || train.dest < 0) continue;
    const path = trainRoute(industry, owner, start, train.heading ?? -1, train.dest);
    if (!path || path.length === 0) {
      engine.train = { ...train, dest: -1 };
      continue;
    }
    if (coalIn(engine) < TRAINS.coalPerPoint) continue;
    let consist = [start, ...(train.cars ?? [])];
    const free = (sq: number): boolean => {
      if (pieceAt(position, sq) !== 0) return false;
      // Own track, or the consist's own tail, which vacates as it steps.
      const there = structureAt(industry, sq);
      if (there?.kind === TRACK && there.owner === owner) return true;
      return consist.length > 1 && sq === consist[consist.length - 1];
    };
    if (!free(path[0])) continue;
    burnCoal(engine, TRAINS.coalPerPoint);
    const exposed = attackedRoyals(position, owner).length;
    for (let i = 0; i < path.length && i < TRAINS.squaresPerPoint; i++) {
      const next = path[i];
      if (!free(next)) break;
      const after = [next, ...consist.slice(0, consist.length - 1)];
      shiftConsist(position, industry, owner, consist, after);
      if (attackedRoyals(position, owner).length > exposed) {
        shiftConsist(position, industry, owner, after, consist);
        break;
      }
      const dir = DIR_OFFSET.indexOf(next - consist[0]);
      consist = after;
      const head = structureAt(industry, next)!;
      head.train = { ...head.train, heading: dir };
      moved++;
    }
    const head = structureAt(industry, consist[0])!;
    if (consist[0] === train.dest) head.train = { ...head.train, dest: -1 };
  }
  if (moved > 0) invalidateMoves(position);
  return moved;
}

// --- riders -----------------------------------------------------------------

/** Reset a car's per-turn counts when the round has moved on. */
function countsNow(train: Train, round: number): { boarded: number; alighted: number } {
  if (train.round !== round) return { boarded: 0, alighted: 0 };
  return { boarded: train.boarded ?? 0, alighted: train.alighted ?? 0 };
}

/** Chebyshev-adjacent. */
const touching = (a: number, b: number): boolean =>
  a !== b && Math.abs(fileOf(a) - fileOf(b)) <= 1 && Math.abs(rankOf(a) - rankOf(b)) <= 1;

/**
 * Why `owner` cannot put the piece on `from` into the car on `car`, or null.
 * Own piece and car, beside it (eight neighbours — the design's "adjacent"),
 * no royal (check detection needs them on the board), nothing holding it in
 * place, room, and fewer than `TRAINS.loadsPerTurn` boardings this round.
 */
export function boardError(
  position: Position,
  industry: IndustryState,
  owner: Color,
  car: number,
  from: number
): TrainError | null {
  const structure = trainAt(industry, car, TRAIN_CAR, owner);
  if (!structure) return "not-your-car";
  const piece = pieceAt(position, from);
  if (!piece || pieceColor(piece) !== owner) return "not-your-piece";
  if (!touching(from, car)) return "not-beside-car";
  if (pieceType(piece) === KING || royalsOf(position, owner).includes(from)) return "royal";
  if (armourLeft(position, from) > 0 || claspedTo(position, from) >= 0) return "held";
  const train = structure.train ?? {};
  if ((train.riders ?? []).length >= TRAINS.carRiders) return "car-full";
  if (countsNow(train, position.round).boarded >= TRAINS.loadsPerTurn) return "loads-spent";
  return null;
}

/**
 * Lift the piece on `from` into the car on `car`. The caller asked
 * `boardError`. Its holdings go down on the square it left, intact; its
 * machine settings stay behind; a costume comes off.
 */
export function boardTrain(position: Position, industry: IndustryState, car: number, from: number): void {
  const structure = structureAt(industry, car)!;
  const rider: Rider = {
    piece: pieceAt(position, from),
    facing: facingAt(position, from),
    hurt: hurtAt(position, from),
    vitality: vitalityAt(position, from),
    plating: platingAt(position, from),
    pieceId: pieceIdAt(position, from),
    variant: variantAt(position, from),
    imitating: imitatingAt(position, from),
    crowned: crownedFrom(position, from)
  };
  for (const store of ["in", "out"] as const) {
    for (let i = 0; i < unitCount(industry, from, store); i++) {
      dropOnGround(industry, from, unitAt(industry, from, i, store));
    }
  }
  for (let i = 0; i < cookingCount(industry, from); i++) dropOnGround(industry, from, cookingUnitAt(industry, from, i));
  stripHoldings(industry, from);
  setHandoff(industry, from, 0);
  clearDump(industry, from);
  setReserve(industry, from, 0);
  setMinedAt(industry, from, -1);
  setLoadedAt(industry, from, -1);
  setPreachedAt(industry, from, -1);
  removePiece(position, from);
  setImitating(position, from, 0);
  const train = structure.train ?? {};
  const counts = countsNow(train, position.round);
  structure.train = {
    ...train,
    riders: [...(train.riders ?? []), rider],
    round: position.round,
    boarded: counts.boarded + 1,
    alighted: counts.alighted
  };
}

/**
 * Why `owner` cannot set rider `index` of the car on `car` down on `to`, or
 * null. Orthogonally beside the car (the design's rule), on the board, no
 * piece there, no obstruction and no enemy building; fewer than
 * `TRAINS.loadsPerTurn` alightings this round.
 */
export function alightError(
  position: Position,
  industry: IndustryState,
  owner: Color,
  car: number,
  to: number,
  index = 0
): TrainError | null {
  const structure = trainAt(industry, car, TRAIN_CAR, owner);
  if (!structure) return "not-your-car";
  const train = structure.train ?? {};
  if ((train.riders ?? [])[index] === undefined) return "car-empty";
  if (!orthogonal(car).includes(to)) return "not-orthogonal";
  if (!hasCell(position, to) || pieceAt(position, to) !== 0 || obstructsAt(position, to)) return "square-blocked";
  const there = position.structures.get(to);
  if (there !== undefined && areEnemies(position, owner as Side, there)) return "square-blocked";
  if (countsNow(train, position.round).alighted >= TRAINS.loadsPerTurn) return "unloads-spent";
  return null;
}

/** Set rider `index` down on `to`. The caller asked `alightError`. */
export function alightTrain(position: Position, industry: IndustryState, car: number, to: number, index = 0): void {
  const structure = structureAt(industry, car)!;
  const train = structure.train ?? {};
  const riders = (train.riders ?? []).slice();
  const [rider] = riders.splice(index, 1);
  setPiece(position, to, rider.piece);
  setFacing(position, to, rider.facing as Direction);
  setHurt(position, to, rider.hurt);
  setVitality(position, to, rider.vitality);
  setPlating(position, to, rider.plating);
  setPieceId(position, to, rider.pieceId);
  setVariant(position, to, rider.variant);
  setImitating(position, to, rider.imitating as PieceType | 0);
  setCrowned(position, to, rider.crowned as PieceType | 0);
  const counts = countsNow(train, position.round);
  structure.train = {
    ...train,
    riders,
    round: position.round,
    boarded: counts.boarded,
    alighted: counts.alighted + 1
  };
}

/** The hash's word for a train's state (§31 S7). Empty off a train. */
export function trainHash(structure: Structure): string {
  const t = structure.train;
  if (!t) return "";
  const riders = (t.riders ?? [])
    .map((r) => [r.piece, r.facing, r.hurt, r.vitality, r.plating, r.variant, r.imitating, r.crowned].join("/"))
    .join(",");
  return `.t${t.heading ?? -1}/${t.dest ?? -1}/${(t.cars ?? []).join("-")}/${t.round ?? -1}/${t.boarded ?? 0}/${t.alighted ?? 0}[${riders}]`;
}
